// StudentExamResultEntry.jsx (FULL UPDATED CODE)
// ✅ Status dropdown (Present/Absent) for BOTH MARKS + GRADE rows
// ✅ Saves to trx: attendance_status (always)
// ✅ If ABSENT -> obtained_marks + obtained_grade cleared + disabled + saved as null

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardTitle, CardBody, Button } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';

import VendorBranchSelector from '../../components/VendorBranchSelector';
import { useSelector } from 'react-redux';
import useVendorBranch from '../../utils/useVendorBranch';
import { apiService } from '../../constants/ApiService';

const EVALUATED_BY = { MARKS: 'MARKS', GRADE: 'GRADE' };

// ✅ Attendance Status
const ATT_STATUS = { PRESENT: 'PRESENT', ABSENT: 'ABSENT' };

const GRADE_SCALE = ['A+', 'A', 'B+', 'B', 'C+', 'C', 'D', 'E', 'F'].map(
  (g) => ({
    value: g,
    label: g,
  }),
);

const normalizeEval = (v) => {
  const s = String(v || '')
    .trim()
    .toUpperCase();
  return s === EVALUATED_BY.GRADE ? EVALUATED_BY.GRADE : EVALUATED_BY.MARKS;
};

const normalizeAtt = (v) => {
  const s = String(v || '')
    .trim()
    .toUpperCase();
  return s === ATT_STATUS.ABSENT ? ATT_STATUS.ABSENT : ATT_STATUS.PRESENT;
};

const getCaseInsensitive = (obj, key) => {
  if (!obj || typeof obj !== 'object') return undefined;
  const foundKey = Object.keys(obj).find(
    (k) => k.toLowerCase() === String(key).toLowerCase(),
  );
  return foundKey ? obj[foundKey] : undefined;
};

const normalizeArray = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const keys = [
    'Data',
    'data',
    'List',
    'list',
    'Result',
    'result',
    'Items',
    'items',
  ];
  for (const k of keys) {
    const v = getCaseInsensitive(payload, k);
    if (Array.isArray(v)) return v;
  }

  const nested1 = getCaseInsensitive(payload, 'data');
  const nested2 = getCaseInsensitive(payload, 'Data');

  if (nested1 && typeof nested1 === 'object') {
    const a = normalizeArray(nested1);
    if (a.length) return a;
  }
  if (nested2 && typeof nested2 === 'object') {
    const a = normalizeArray(nested2);
    if (a.length) return a;
  }

  return [];
};

const pick = (...vals) =>
  vals.find((v) => v !== undefined && v !== null && v !== '');

const toNumOrNull = (v) => {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const isEmpty = (v) => String(v ?? '').trim() === '';

const normalizeCourseStructure = (raw) => {
  const s = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (!s) return 'semester';
  if (s.includes('year')) return 'year';
  if (s.includes('month')) return 'month';
  return 'semester';
};

const structureWordUpper = (raw) => {
  const s = normalizeCourseStructure(raw);
  if (s === 'year') return 'YEAR';
  if (s === 'month') return 'MONTH';
  return 'SEMESTER';
};

const structureHeader = (raw, index1Based) =>
  `${structureWordUpper(raw)} - ${index1Based}`;

// ---------------- Pattern pickers ----------------
const pickPatternId = (p) =>
  Number(
    p?.id ??
      p?.ID ??
      getCaseInsensitive(p, 'id') ??
      getCaseInsensitive(p, 'ID') ??
      0,
  );

const pickPatternSemesterNo = (p) =>
  Number(
    p?.semester_no ??
      p?.Semester_No ??
      p?.semester ??
      p?.Semester ??
      p?.sem_no ??
      p?.Sem_No ??
      getCaseInsensitive(p, 'semester_no') ??
      getCaseInsensitive(p, 'semester') ??
      getCaseInsensitive(p, 'sem_no') ??
      0,
  );

const pickTermName = (p) =>
  pick(
    p?.term_name,
    p?.termName,
    p?.Term_Name,
    getCaseInsensitive(p, 'term_name'),
    getCaseInsensitive(p, 'termName'),
    getCaseInsensitive(p, 'Term_Name'),
    '',
  );

const normalizeTermType = (raw) => {
  const s = String(raw || '')
    .trim()
    .toUpperCase();
  if (!s) return '';
  if (s.includes('MID')) return 'MIDTERM';
  if (s.includes('INTERNAL')) return 'INTERNAL';
  if (s.includes('UNIT')) return 'UNIT';
  if (s.includes('FINAL')) return 'FINAL';
  if (s.includes('SEM')) return 'SEMESTER';
  if (s.includes('END')) return 'SEMESTER';
  return s;
};

const pickTermType = (p) => {
  const direct =
    p?.term_type ??
    p?.termType ??
    p?.exam_term ??
    p?.examTerm ??
    getCaseInsensitive(p, 'term_type') ??
    getCaseInsensitive(p, 'termType') ??
    getCaseInsensitive(p, 'exam_term') ??
    getCaseInsensitive(p, 'examTerm') ??
    '';
  const inferred = direct || pickTermName(p) || '';
  return normalizeTermType(inferred);
};

// ✅ API may return "Grade"/"grade" instead of "evaluated_by"
const pickEvaluatedBy = (p) => {
  const v =
    p?.evaluated_by ??
    p?.evaluatedBy ??
    p?.grade ??
    p?.Grade ??
    getCaseInsensitive(p, 'evaluated_by') ??
    getCaseInsensitive(p, 'evaluatedBy') ??
    getCaseInsensitive(p, 'grade') ??
    getCaseInsensitive(p, 'Grade') ??
    '';
  return normalizeEval(v);
};

const pickPatternTotalMarks = (p) =>
  pick(
    p?.total_marks,
    p?.totalMarks,
    getCaseInsensitive(p, 'total_marks'),
    getCaseInsensitive(p, 'totalMarks'),
    '',
  );

const pickPatternPassingMarks = (p) =>
  pick(
    p?.passing_marks,
    p?.passingMarks,
    getCaseInsensitive(p, 'passing_marks'),
    getCaseInsensitive(p, 'passingMarks'),
    '',
  );

const pickPatternPassingGrade = (p) =>
  pick(
    p?.passing_grade,
    p?.passingGrade,
    getCaseInsensitive(p, 'passing_grade'),
    getCaseInsensitive(p, 'passingGrade'),
    '',
  );

// ---------------- Subject pickers ----------------
const pickSubjectId = (s) =>
  Number(
    s?.subject_id ??
      s?.SubjectId ??
      s?.Subject_ID ??
      s?.subjectId ??
      s?.SubjectID ??
      s?.subject_master_id ??
      s?.subjectMasterId ??
      s?.SubjectMasterId ??
      s?.subject?.id ??
      s?.subject?.subject_id ??
      s?.Subject?.id ??
      s?.Subject?.subject_id ??
      0,
  ) || 0;

const pickSubjectName = (s) =>
  String(
    s?.subject_name ??
      s?.Subject_Name ??
      s?.name ??
      s?.Name ??
      s?.subjectName ??
      s?.subject?.subject_name ??
      s?.subject?.name ??
      s?.Subject?.subject_name ??
      s?.Subject?.name ??
      '',
  );

const pickSubjectCode = (s) =>
  String(
    s?.subject_code ??
      s?.Subject_Code ??
      s?.code ??
      s?.Code ??
      s?.subjectCode ??
      s?.subject?.subject_code ??
      s?.subject?.code ??
      s?.Subject?.subject_code ??
      s?.Subject?.code ??
      '',
  );

// ✅ supports sem_no / Sem_No
const pickSubjectSemesterNo = (s) =>
  Number(
    s?.semester ??
      s?.Semester ??
      s?.semester_no ??
      s?.Semester_No ??
      s?.semesterNo ??
      s?.sem_no ??
      s?.Sem_No ??
      getCaseInsensitive(s, 'semester') ??
      getCaseInsensitive(s, 'semester_no') ??
      getCaseInsensitive(s, 'semesterNo') ??
      getCaseInsensitive(s, 'sem_no') ??
      getCaseInsensitive(s, 'Sem_No') ??
      1,
  ) || 1;

// ✅ pick best grade from UI rows
const pickBestGradeFromRows = (rows) => {
  const order = {
    'A+': 1,
    A: 2,
    'B+': 3,
    B: 4,
    'C+': 5,
    C: 6,
    D: 7,
    E: 8,
    F: 9,
  };

  const grades = (rows || [])
    .filter((r) => normalizeEval(r.evaluated_by) === EVALUATED_BY.GRADE)
    .map((r) =>
      String(r.obtained_grade || '')
        .trim()
        .toUpperCase(),
    )
    .filter(Boolean);

  if (!grades.length) return null;
  grades.sort((g1, g2) => (order[g1] ?? 999) - (order[g2] ?? 999));
  return grades[0] || null;
};

const StudentExamResultEntry = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const meta = useMemo(() => state || {}, [state]);

  const { user } = useSelector((s) => s.authState);
  const { isSuperAdmin, isAdmin, hasMultiBranch } = useVendorBranch(user);

  const [loading, setLoading] = useState(false);
  const [errors] = useState({});

  const [form, setForm] = useState({
    vendor_id: meta.vendor_id || 0,
    branch_id: meta.branch_id || 0,

    class_id: meta.class_id || 0,
    class_name: meta.class_name || '',

    batch_id: meta.batch_id || 0,
    batch_name: meta.batch_name || '',
    batch_code: meta.batch_code || '',

    batch_exam_id: meta.batch_exam_id || 0,

    student_id: meta.student_id || 0,
    student_name: meta.student_name || '',
    roll: meta.roll || '',

    course_structure: meta.course_structure || '',
    total_semester: meta.total_semester || '',
    enrollment_id: meta.enrollment_id || 0,

    sem_no: Number(meta.sem_no || meta.semNo || meta.semester_no || 0) || 0,
  });

  const [patternList, setPatternList] = useState([]);
  const [termSubjects, setTermSubjects] = useState({});
  const [courseStructure, setCourseStructure] = useState('semester');
  const [totalSemester, setTotalSemester] = useState(0);
  const [hasExisting, setHasExisting] = useState(false);

  const reqRef = useRef(0);
  const LIST_ROUTE = '/list-stud-result';

  // Totals helper (MARKS only; ABSENT ignored because obtained null)
  const calcTotals = (rows) => {
    const marksRows = (rows || []).filter(
      (r) => normalizeEval(r.evaluated_by) === EVALUATED_BY.MARKS,
    );

    const totalMax = marksRows.reduce(
      (sum, r) => sum + (toNumOrNull(r.max_marks) ?? 0),
      0,
    );
    const totalObt = marksRows.reduce(
      (sum, r) => sum + (toNumOrNull(r.obtained_marks) ?? 0),
      0,
    );

    return { totalMax, totalObt };
  };

  const allRowsFlat = useMemo(
    () => Object.values(termSubjects || {}).flat(),
    [termSubjects],
  );
  const grand = useMemo(() => calcTotals(allRowsFlat), [allRowsFlat]);

  const goBackWithState = () => {
    navigate(LIST_ROUTE, {
      state: {
        vendor_id: form.vendor_id,
        branch_id: form.branch_id,
        class_id: form.class_id,
        class_name: form.class_name,
        batch_id: form.batch_id,
        batch_name: form.batch_name,
        batch_code: form.batch_code,
        batch_exam_id: form.batch_exam_id,
        course_structure: form.course_structure,
        total_semester: form.total_semester,
        sem_no: form.sem_no || 0,
      },
    });
  };

  useEffect(() => {
    if (!form?.student_id) {
      toast.error('Student data नहीं मिली। कृपया Student List से खोलें।');
      navigate(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.student_id]);

  const validateVendorBranch = () => {
    if (isSuperAdmin && (!form.vendor_id || Number(form.vendor_id) <= 0)) {
      toast.error('Vendor चुनें');
      return false;
    }
    if (
      (isSuperAdmin || isAdmin || hasMultiBranch) &&
      (!form.branch_id || Number(form.branch_id) <= 0)
    ) {
      toast.error('Branch चुनें');
      return false;
    }
    return true;
  };

  // ---------------- API loaders ----------------
  const loadPatterns = async (v, b, courseId) => {
    const url = `api/ExamMaster/GetAllCourseExamPattern?vendorId=${v}&branchId=${b}&courseId=${courseId}&search=`;
    const res = await apiService.get(url);
    if (res?.status !== true) return [];
    return normalizeArray(res) || [];
  };

  const loadAllCourseSubPatterns = async (v, b, courseId) => {
    const url =
      `api/ExamMaster/GetAllCourseExamSubPattern?vendorId=${v}` +
      `&branchId=${b}` +
      `&courseId=${courseId}` +
      `&patternId=` +
      `&subjectId=` +
      `&search=`;
    const res = await apiService.get(url);
    if (res?.status !== true) return [];
    return normalizeArray(res) || [];
  };

  const loadClassSubjects = async (v, b, courseId) => {
    const url = `api/ExamMaster/GetCourseSubjectsWithPattern?vendorId=${v}&branchId=${b}&classId=${courseId}`;
    const res = await apiService.get(url);
    if (res?.status !== true) return [];
    return normalizeArray(res) || [];
  };

  const loadExistingStudentMarks = async (v, b) => {
    const url =
      `api/ExamMaster/GetStudentExamResultEntryTrx?vendorId=${v}` +
      `&branchId=${b}` +
      `&batchExamId=${form.batch_exam_id}` +
      `&studentId=${form.student_id}`;

    const res = await apiService.get(url);
    if (res?.status !== true) return [];
    return res?.Data1 || res?.data1?.Data1 || res?.data1 || [];
  };

  // ---------------- build termSubjects ----------------
  const buildTermSubjects = (
    patterns,
    subjectsAll,
    subPatterns,
    existingMarks,
  ) => {
    const exMap = {};
    (existingMarks || []).forEach((x) => {
      const pid = Number(x.pattern_id || 0);
      const sid = Number(x.subject_id || 0);
      if (pid && sid) exMap[`${pid}|${sid}`] = x;
    });

    const spMap = {};
    (subPatterns || []).forEach((x) => {
      const pid = Number(
        x.pattern_id ?? x.patternId ?? getCaseInsensitive(x, 'pattern_id') ?? 0,
      );
      const sid = Number(
        x.subject_id ?? x.subjectId ?? getCaseInsensitive(x, 'subject_id') ?? 0,
      );
      if (pid && sid) spMap[`${pid}|${sid}`] = x;
    });

    const patMap = {};
    (patterns || []).forEach((p) => {
      const pid = pickPatternId(p);
      if (!pid) return;

      patMap[pid] = {
        term_name: pickTermName(p) || pickTermType(p) || 'Pattern',
        term_type: pickTermType(p) || pickTermName(p) || String(pid),
        semester_no: pickPatternSemesterNo(p) || 0,
        evaluated_by: pickEvaluatedBy(p),
        total_marks: pickPatternTotalMarks(p) || '',
        passing_marks: pickPatternPassingMarks(p) || '',
        passing_grade: pickPatternPassingGrade(p) || '',
      };
    });

    const grouped = {};
    Object.keys(patMap).forEach((pidStr) => {
      const pid = Number(pidStr);
      const pmeta = patMap[pid] || {};

      grouped[pid] = (subjectsAll || [])
        .map((s) => {
          const sid = pickSubjectId(s);
          if (!sid) return null;

          const semNo = pickSubjectSemesterNo(s) || 1;

          const key = `${pid}|${sid}`;
          const sp = spMap[key];
          const ex = exMap[key];

          const mode = normalizeEval(
            sp?.evaluated_by ??
              sp?.evaluatedBy ??
              sp?.grade ??
              sp?.Grade ??
              getCaseInsensitive(sp, 'evaluated_by') ??
              getCaseInsensitive(sp, 'evaluatedBy') ??
              getCaseInsensitive(sp, 'grade') ??
              getCaseInsensitive(sp, 'Grade') ??
              pmeta.evaluated_by ??
              'MARKS',
          );

          const originalMode = normalizeEval(
            ex?.evaluated_by || ex?.grade || 'MARKS',
          );
          const modeChanged = Number(ex?.id || 0) > 0 && originalMode !== mode;

          // ✅ attendance_status read from existing (if any) - FOR BOTH MODES
          const exAtt =
            ex?.attendance_status ??
            ex?.Attendance_Status ??
            ex?.status ??
            ex?.Status ??
            getCaseInsensitive(ex, 'attendance_status') ??
            getCaseInsensitive(ex, 'Attendance_Status') ??
            getCaseInsensitive(ex, 'status') ??
            getCaseInsensitive(ex, 'Status') ??
            'PRESENT';

          const attStatus = normalizeAtt(exAtt); // ✅ for MARKS + GRADE both

          return {
            tempId: `${form.student_id}_${pid}_${sid}`,
            id: Number(ex?.id || 0),

            pattern_id: pid,
            subject_id: sid,
            semester_no: semNo,

            term_name: pmeta.term_name,
            term_type: pmeta.term_type,

            subject_name: pickSubjectName(s),
            subject_code: pickSubjectCode(s),

            evaluated_by: mode,
            original_mode: Number(ex?.id || 0) > 0 ? originalMode : null,

            max_marks:
              mode === EVALUATED_BY.MARKS
                ? (sp?.max_marks ?? pmeta.total_marks ?? '')
                : '',
            passing_marks:
              mode === EVALUATED_BY.MARKS
                ? (sp?.passing_marks ?? pmeta.passing_marks ?? '')
                : '',
            passing_grade:
              mode === EVALUATED_BY.GRADE
                ? (sp?.passing_grade ?? pmeta.passing_grade ?? '')
                : '',

            obtained_marks:
              mode === EVALUATED_BY.MARKS
                ? modeChanged
                  ? ''
                  : (ex?.obtained_marks ?? '')
                : '',
            obtained_grade:
              mode === EVALUATED_BY.GRADE
                ? modeChanged
                  ? ''
                  : (ex?.obtained_grade ?? '')
                : '',

            // ✅ NEW: always present/absent
            attendance_status: attStatus,

            error_obtained: '',
          };
        })
        .filter(Boolean);
    });

    setTermSubjects(grouped);

    const maxSem = Math.max(
      0,
      ...(subjectsAll || []).map((s) => pickSubjectSemesterNo(s) || 1),
    );
    setTotalSemester(Number(form.total_semester || 0) || maxSem || 0);
  };

  // ---------------- LOAD ALL ----------------
  const loadAll = async () => {
    if (!validateVendorBranch()) return;

    const v = Number(form.vendor_id || 0);
    const b = Number(form.branch_id || 0);
    const courseId = Number(form.class_id || 0);

    if (!v || !b || !courseId) {
      toast.error('Vendor/Branch/Course missing है।');
      return;
    }

    const reqId = ++reqRef.current;

    try {
      setLoading(true);
      setCourseStructure(
        normalizeCourseStructure(form.course_structure || 'semester'),
      );

      const patterns = await loadPatterns(v, b, courseId);
      if (reqRef.current !== reqId) return;
      setPatternList(patterns || []);
      if (!patterns.length)
        toast.error('इस course के लिए Exam Pattern नहीं मिला।');

      const subPatterns = await loadAllCourseSubPatterns(v, b, courseId);
      if (reqRef.current !== reqId) return;
      if (!subPatterns.length) {
        toast.error(
          'Subject Pattern (Max/Passing) नहीं मिला। पहले Course Exam Subject Pattern configure करें।',
        );
      }

      const subjects = await loadClassSubjects(v, b, courseId);
      if (reqRef.current !== reqId) return;
      if (!subjects.length) {
        toast.error('इस course में subjects नहीं मिले।');
        setTermSubjects({});
        setTotalSemester(0);
        setHasExisting(false);
        return;
      }

      const existing = await loadExistingStudentMarks(v, b);
      if (reqRef.current !== reqId) return;

      setHasExisting((existing || []).length > 0);
      buildTermSubjects(patterns, subjects, subPatterns, existing);
    } catch (e) {
      console.error(e);
      toast.error('Data load failed.');
    } finally {
      if (reqRef.current === reqId) setLoading(false);
    }
  };

  useEffect(() => {
    if (!form?.student_id) return;
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.vendor_id, form.branch_id, form.student_id]);

  // ---------------- term types + map ----------------
  const termTypes = useMemo(() => {
    const set = new Set();
    (patternList || []).forEach((p) => {
      const tt = pickTermType(p);
      if (tt) set.add(tt);
    });
    if (set.size === 0) return [];

    const ordered = [];
    ['MIDTERM', 'INTERNAL', 'UNIT', 'SEMESTER', 'FINAL'].forEach((k) => {
      if (set.has(k)) ordered.push(k);
      set.delete(k);
    });
    Array.from(set).forEach((k) => ordered.push(k));
    return ordered;
  }, [patternList]);

  const patternBySemTerm = useMemo(() => {
    const map = {};
    (patternList || []).forEach((p) => {
      const pid = pickPatternId(p);
      if (!pid) return;
      const sem = pickPatternSemesterNo(p) || 0;
      const tt = pickTermType(p) || String(pid);
      map[`${sem}|${tt}`] = p;
    });
    return map;
  }, [patternList]);

  // ✅ update row helper (marks/grade/status)
  const updateObtained = (patternId, subjectId, patch) => {
    setTermSubjects((prev) => {
      const list = [...(prev[patternId] || [])];
      const idx = list.findIndex(
        (x) => Number(x.subject_id) === Number(subjectId),
      );
      if (idx < 0) return prev;

      const row = { ...list[idx], ...patch };

      const mode = normalizeEval(row.evaluated_by);

      // grade/marks mutual exclusive
      if (mode === EVALUATED_BY.GRADE && 'obtained_grade' in patch)
        row.obtained_marks = '';
      if (mode === EVALUATED_BY.MARKS && 'obtained_marks' in patch)
        row.obtained_grade = '';

      row.error_obtained = '';

      // ✅ status always normalized for both modes
      row.attendance_status = normalizeAtt(row.attendance_status || 'PRESENT');

      // ✅ ABSENT => clear obtained for BOTH MARKS + GRADE and skip validations
      if (row.attendance_status === ATT_STATUS.ABSENT) {
        row.obtained_marks = '';
        row.obtained_grade = '';
        row.error_obtained = '';
        list[idx] = row;
        return { ...prev, [patternId]: list };
      }

      // marks validation (only when PRESENT + MARKS)
      if (mode === EVALUATED_BY.MARKS && 'obtained_marks' in patch) {
        const obt = toNumOrNull(row.obtained_marks);
        const max = toNumOrNull(row.max_marks);

        if (obt !== null) {
          if (obt < 0) {
            row.obtained_marks = '0';
            row.error_obtained = 'Obtained marks 0 से कम नहीं हो सकते।';
          }
          if (max !== null && obt > max) {
            row.error_obtained =
              'Obtained marks, Max marks से ज्यादा नहीं हो सकते।';
          }
        }
      }

      list[idx] = row;
      return { ...prev, [patternId]: list };
    });
  };

  const validateMarks = () => {
    const all = Object.values(termSubjects || {}).flat();
    const bad = all.find((r) => {
      const mode = normalizeEval(r.evaluated_by);
      if (mode === EVALUATED_BY.GRADE) return false;

      const att = normalizeAtt(r.attendance_status || 'PRESENT');
      if (att === ATT_STATUS.ABSENT) return false; // ✅ absent skip

      const obt = toNumOrNull(r.obtained_marks);
      if (obt === null) return false;

      const max = toNumOrNull(r.max_marks);
      return max !== null && obt > max;
    });

    return !bad;
  };

  // ---------------- SAVE ----------------
  const onSave = async () => {
    if (!validateVendorBranch()) return;

    if (!validateMarks()) {
      toast.error('कृपया invalid obtained marks ठीक करें।');
      return;
    }

    try {
      setLoading(true);

      const allRows = Object.values(termSubjects || {}).flat();

      const reqRows = allRows
        .filter((r) => {
          const mode = normalizeEval(r.evaluated_by);
          const att = normalizeAtt(r.attendance_status || 'PRESENT');

          // ✅ If ABSENT, still save
          if (att === ATT_STATUS.ABSENT) return true;

          const hasObtained =
            mode === EVALUATED_BY.GRADE
              ? !isEmpty(r.obtained_grade)
              : !isEmpty(r.obtained_marks);

          const modeChanged =
            Number(r.id || 0) > 0 &&
            r.original_mode &&
            normalizeEval(r.original_mode) !== mode;

          return hasObtained || modeChanged;
        })
        .map((r) => {
          const mode = normalizeEval(r.evaluated_by);
          const att = normalizeAtt(r.attendance_status || 'PRESENT');

          return {
            id: r.id > 0 ? Number(r.id) : null,
            pattern_id: Number(r.pattern_id),
            subject_id: Number(r.subject_id),
            evaluated_by: mode,
            sem_no: Number(r.semester_no || 0),

            // ✅ ALWAYS SAVE STATUS (MARKS + GRADE)
            attendance_status: att,
            subject_name: String(r.subject_name || '').trim(),
            max_marks:
              mode === EVALUATED_BY.MARKS ? toNumOrNull(r.max_marks) : null,
            passing_marks:
              mode === EVALUATED_BY.MARKS ? toNumOrNull(r.passing_marks) : null,
            passing_grade:
              mode === EVALUATED_BY.GRADE
                ? String(r.passing_grade || '')
                : null,

            obtained_marks:
              mode === EVALUATED_BY.MARKS
                ? att === ATT_STATUS.ABSENT
                  ? null
                  : toNumOrNull(r.obtained_marks)
                : null,

            obtained_grade:
              mode === EVALUATED_BY.GRADE
                ? att === ATT_STATUS.ABSENT
                  ? null
                  : String(r.obtained_grade || '')
                : null,
          };
        });

      if (!reqRows.length) {
        toast.error('किसी भी subject में data नहीं भरा गया।');
        return;
      }

      const overallGrade = pickBestGradeFromRows(allRows);

      const body = {
        vendor_id: Number(form.vendor_id),
        branch_id: Number(form.branch_id),
        batch_exam_id: Number(form.batch_exam_id),
        student_id: Number(form.student_id),
        course_id: Number(form.class_id || 0),

        batch_id: Number(form.batch_id || 0),
        enrollment_id: Number(meta.enrollment_id || form.enrollment_id || 0),

        grade: overallGrade,
        req: reqRows,
      };

      const res = await apiService.post(
        'api/ExamMaster/UpsertStudentExamResultBulk',
        body,
      );

      if (res?.status === true) {
        toast.success(res?.message || res?.Message || 'Saved successfully');
        goBackWithState();
      } else {
        toast.error(res?.message || res?.Message || 'Save failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Save करते समय error हुआ।');
    } finally {
      setLoading(false);
    }
  };

  const renderPatternSection = (semNum, termKey) => {
    const p =
      patternBySemTerm[`${semNum}|${termKey}`] ||
      patternBySemTerm[`0|${termKey}`] ||
      null;

    const pid = p ? pickPatternId(p) : 0;
    const title =
      (p && (pickTermName(p) || termKey)) ||
      (termKey ? String(termKey) : 'Pattern');

    const allRows = pid ? termSubjects[pid] || [] : [];
    const rows = allRows.filter(
      (r) => Number(r.semester_no || 1) === Number(semNum),
    );

    if (!p || !pid) return null;

    const hasMarks = rows.some(
      (r) => normalizeEval(r.evaluated_by) === EVALUATED_BY.MARKS,
    );
    const hasGrade = rows.some(
      (r) => normalizeEval(r.evaluated_by) === EVALUATED_BY.GRADE,
    );

    const { totalMax, totalObt } = calcTotals(rows);
    const colCount = 4 + (hasMarks ? 3 : 0) + (hasGrade ? 1 : 0) + 1;

    return (
      <div className='mb-4' key={`${semNum}_${termKey}`}>
        <h5 className='text-primary mb-2'>{title}</h5>

        <table className='table table-bordered table-hover'>
          <thead className='bg-primary text-white'>
            <tr>
              <th style={{ width: 80 }}>S. no.</th>
              <th>Subject Name</th>
              <th style={{ width: 200 }}>Subject Code</th>
              <th style={{ width: 160 }}>Evaluated By</th>

              {hasMarks ? (
                <>
                  <th style={{ width: 140 }}>Max Marks</th>
                  <th style={{ width: 140 }}>Passing Marks</th>
                  <th style={{ width: 160 }}>Status</th>
                </>
              ) : (
                <th style={{ width: 160 }}>Status</th>
              )}

              {hasGrade ? <th style={{ width: 160 }}>Passing Grade</th> : null}

              <th style={{ width: 220 }}>Obtained</th>
            </tr>
          </thead>

          <tbody>
            {rows.length ? (
              <>
                {rows.map((r, i) => {
                  const mode = normalizeEval(r.evaluated_by);
                  const isGrade = mode === EVALUATED_BY.GRADE;

                  const att = normalizeAtt(r.attendance_status || 'PRESENT');
                  const isAbsent = att === ATT_STATUS.ABSENT;

                  return (
                    <tr key={r.tempId}>
                      <td>{i + 1}</td>

                      <td>
                        <input
                          type='text'
                          className='form-control'
                          value={r.subject_name || ''}
                          readOnly
                        />
                      </td>

                      <td>
                        <input
                          type='text'
                          className='form-control'
                          value={r.subject_code || ''}
                          readOnly
                        />
                      </td>

                      <td>
                        <input
                          type='text'
                          className='form-control'
                          value={mode}
                          readOnly
                        />
                      </td>

                      {hasMarks ? (
                        <>
                          <td>
                            <input
                              type='text'
                              className='form-control'
                              value={!isGrade ? (r.max_marks ?? '') : ''}
                              readOnly
                            />
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control'
                              value={!isGrade ? (r.passing_marks ?? '') : ''}
                              readOnly
                            />
                          </td>

                          {/* ✅ Status dropdown for BOTH modes */}
                          <td>
                            <select
                              className='form-control'
                              value={att}
                              onChange={(e) =>
                                updateObtained(pid, r.subject_id, {
                                  attendance_status: e.target.value,
                                })
                              }
                            >
                              <option value='PRESENT'>Present</option>
                              <option value='ABSENT'>Absent</option>
                            </select>
                          </td>
                        </>
                      ) : (
                        <td>
                          <select
                            className='form-control'
                            value={att}
                            onChange={(e) =>
                              updateObtained(pid, r.subject_id, {
                                attendance_status: e.target.value,
                              })
                            }
                          >
                            <option value='PRESENT'>Present</option>
                            <option value='ABSENT'>Absent</option>
                          </select>
                        </td>
                      )}

                      {hasGrade ? (
                        <td>
                          <input
                            type='text'
                            className='form-control'
                            value={isGrade ? (r.passing_grade ?? '') : ''}
                            readOnly
                          />
                        </td>
                      ) : null}

                      <td>
                        {!isGrade ? (
                          <>
                            <input
                              type='number'
                              className={`form-control ${r.error_obtained ? 'is-invalid' : ''}`}
                              value={r.obtained_marks ?? ''}
                              placeholder={
                                isAbsent ? 'Absent' : 'Obtained marks'
                              }
                              min={0}
                              max={toNumOrNull(r.max_marks) ?? undefined}
                              disabled={isAbsent}
                              onChange={(e) =>
                                updateObtained(pid, r.subject_id, {
                                  obtained_marks: e.target.value,
                                })
                              }
                            />

                            {r.error_obtained ? (
                              <small className='text-danger'>
                                {r.error_obtained}
                              </small>
                            ) : (
                              <small className='text-muted'>
                                {isAbsent
                                  ? 'Absent'
                                  : `${toNumOrNull(r.obtained_marks) ?? 0}/${toNumOrNull(r.max_marks) ?? 0}`}
                              </small>
                            )}
                          </>
                        ) : (
                          <Select
                            options={GRADE_SCALE}
                            value={GRADE_SCALE.find(
                              (g) =>
                                String(g.value) ===
                                String(r.obtained_grade || ''),
                            )}
                            onChange={(opt) =>
                              updateObtained(pid, r.subject_id, {
                                obtained_grade: opt?.value || '',
                              })
                            }
                            isDisabled={isAbsent}
                            isClearable
                            isSearchable={false}
                            placeholder={isAbsent ? 'Absent' : 'Select Grade'}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (base) => ({ ...base, minHeight: 34 }),
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}

                <tr style={{ fontWeight: 700, background: '#f8f9fa' }}>
                  <td
                    colSpan={4}
                    className='text-end'
                    style={{ verticalAlign: 'middle' }}
                  >
                    Total
                  </td>

                  {hasMarks ? (
                    <>
                      <td style={{ verticalAlign: 'middle' }}>{totalMax}</td>
                      <td></td>
                      <td></td>
                    </>
                  ) : null}

                  {hasGrade ? <td></td> : null}

                  <td style={{ verticalAlign: 'middle' }}>
                    {hasMarks ? `${totalObt}/${totalMax}` : '-'}
                  </td>
                </tr>
              </>
            ) : (
              <tr>
                <td colSpan={colCount} className='text-center text-muted'>
                  No subjects found for {structureWordUpper(courseStructure)}{' '}
                  {semNum} - {title}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const selectedSem = Number(meta.sem_no || form.sem_no || 0) || 0;
  const semList =
    totalSemester > 0
      ? selectedSem > 0
        ? [selectedSem]
        : Array.from({ length: totalSemester }, (_, i) => i + 1)
      : [];

  return (
    <>
      <VendorBranchSelector
        user={user}
        selectionData={form}
        setSelectionData={setForm}
        accessButton={true}
        errorMessage={errors}
      />

      <Card className='card-box pd-20 mb-20'>
        <CardTitle className='border-bottom p-3 d-flex justify-content-between align-items-center'>
          <div>
            <div style={{ fontWeight: 700 }}>
              Student Exam Result Entry{' '}
              <span className='text-muted' style={{ fontWeight: 600 }}>
                ({hasExisting ? 'EDIT' : 'ADD'})
              </span>
            </div>

            <div className='text-muted' style={{ fontSize: 13 }}>
              {form.student_name || ''} {form.roll ? `(${form.roll})` : ''}
            </div>

            {totalSemester ? (
              <div className='text-muted' style={{ fontSize: 13 }}>
                Total {structureWordUpper(courseStructure)}:{' '}
                <b>{totalSemester}</b>
                {selectedSem > 0 ? (
                  <>
                    {' '}
                    | Selected {structureWordUpper(courseStructure)}:{' '}
                    <b>{selectedSem}</b>
                  </>
                ) : null}
              </div>
            ) : null}

            <div className='text-muted' style={{ fontSize: 13 }}>
              Grand Total:{' '}
              <b>
                {grand.totalObt}/{grand.totalMax}
              </b>
            </div>
          </div>

          <div className='d-flex gap-2'>
            <Button
              color='secondary'
              onClick={() => navigate(LIST_ROUTE, { state: meta })}
              disabled={loading}
            >
              <i className='bi bi-arrow-left' /> Back
            </Button>

            <Button color='primary' onClick={onSave} disabled={loading}>
              {loading
                ? hasExisting
                  ? 'Updating...'
                  : 'Saving...'
                : hasExisting
                  ? 'Update'
                  : 'Save'}
            </Button>
          </div>
        </CardTitle>

        <CardBody>
          {loading ? (
            <div className='text-muted'>Loading...</div>
          ) : totalSemester > 0 ? (
            <div className='faq-wrap'>
              <h4 className='mb-30 h4 text-blue padding-top-30'>
                {structureWordUpper(courseStructure)} Wise Exam Subject Pattern
              </h4>

              <div className='padding-bottom-30'>
                {semList.map((semNum) => (
                  <div className='card mb-3' key={`sem_${semNum}`}>
                    <div className='card-header' id={`headingSem${semNum}`}>
                      <button
                        className='btn btn-block w-100 d-flex justify-content-between align-items-center'
                        type='button'
                        data-toggle='collapse'
                        data-target={`#collapseSem${semNum}`}
                        aria-controls={`#collapseSem${semNum}`}
                        style={{ fontWeight: 600 }}
                      >
                        <span>{structureHeader(courseStructure, semNum)}</span>
                        <small className='text-muted'>
                          (Click to show/hide)
                        </small>
                      </button>
                    </div>

                    <div
                      id={`collapseSem${semNum}`}
                      className='collapse show'
                      aria-labelledby={`headingSem${semNum}`}
                    >
                      <div className='card-body'>
                        {termTypes && termTypes.length > 0
                          ? termTypes.map((t) =>
                              renderPatternSection(semNum, t),
                            )
                          : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className='text-muted'>
              No semester/pattern data found. Please configure Course Exam
              Subject Pattern first.
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default StudentExamResultEntry;
